import { Fragment, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useListenort } from './Listenort'
import { normalisiere } from './Suche'
import { angeboten, stillgelegtAus } from './Stilllegung'
import { beschriftung, t } from './sprache'
import type { Beschriftung } from './typen'

/**
 * Die Auswahlliste, in die man hineintippen kann (7.2 Abschnitt 10,
 * R583–R587).
 *
 * **Eine Klappliste bleibt es.** Wer nie tippt, merkt keinen Unterschied: Er
 * klickt, die Liste steht offen, er wählt. Das Tippen kommt dazu und nimmt
 * nichts weg — und es kommt dazu, weil dieselbe Liste beim Entwickler drei
 * Einträge hat und beim Kunden zweihundert (R583 — Eine Auswahlliste, deren
 * Vorrat aus gepflegten Daten kommt, lässt sich durchsuchen).
 *
 * **Getippt wird gesucht, nicht eingetragen** (R585). Der Text im Feld ist ein
 * Filter und wird nie zum Wert; wer die Liste verlässt, ohne zu wählen, behält,
 * was vorher gewählt war. Damit bleibt R327 (Ein Verweis wird ausgesucht, nicht
 * getippt) unangetastet — ein Tippfehler kann hier nicht auf etwas zeigen, das
 * es nicht gibt.
 *
 * **Die Liste hängt am Dokument und nicht im Feld.** Derselbe Griff wie beim
 * Leistenmenü: Ein Feld in einer Tabellenzelle ist 90 px breit, die Liste
 * darunter braucht den Namen der Gesellschaft. In der Zelle gezeichnet wäre sie
 * abgeschnitten.
 */

/** Ein Eintrag der Liste. */
export type Auswahleintrag = {
  /** Was gespeichert wird, wenn er gewählt ist. */
  wert: string

  /** Was dasteht — meist `Code — Bezeichnung`. Es wird mitgesucht. */
  text: string

  /**
   * Was zusätzlich durchsucht wird, aber **nicht** dasteht: Kürzel, Schlüssel,
   * die zweite Sprache eines Namens.
   *
   * Der Anwender kennt von einer Gesellschaft mal den Code, mal den Namen, mal
   * das Kürzel — und je nach Haus ist es ein anderes davon (R584).
   */
  suchtext?: string

  /**
   * Die Gruppe, unter der der Eintrag steht — eine Überschrift mit Strich
   * davor, sobald sie wechselt (2.13 Abschnitt 14, R1115). Ohne Gruppe keine
   * Überschrift.
   */
  gruppe?: string

  /**
   * Ab welcher Periode das Stammdatum nicht mehr gilt (R774, R1447) — dann
   * bietet die Liste es nicht mehr an. Gesetzt von `ausStammdatum`.
   */
  stillgelegtAb?: number | null
}

/**
 * Ein Stammdatum als Eintrag der Liste — der eine Handgriff, den fast jede
 * Aufrufstelle braucht.
 *
 * **Dastehen tun Code und Bezeichnung, mitgesucht werden Kürzel, Schlüssel und
 * die übrigen Sprachen** (R584). Es sind dieselben Angaben, über die die
 * Stammdatenliste links sucht (`passend()` in `Stammdaten.tsx`) — ein zweites
 * Suchverständnis in derselben Anwendung gibt es nicht.
 */
export function ausStammdatum(e: {
  schluessel: string
  code: string
  namen: Record<string, string>
  kuerzel?: Record<string, string> | null
  werte?: Record<string, string>
}): Auswahleintrag {
  // **Das Stillgelegt ab reist mit** (R1447): Die Liste trägt es an jedem
  // Eintrag, und die Auswahl blendet danach aus.
  return {
    wert: e.schluessel,
    text: mitCode(e.code, beschriftung(e.namen), ' — ', e.schluessel),
    suchtext: [e.schluessel, ...Object.values(e.namen), ...Object.values(e.kuerzel ?? {})].join(' '),
    stillgelegtAb: stillgelegtAus(e.werte),
  }
}

/**
 * Ob diese Zeichenfolge eine **vergebene Kennung** ist (R551, R699).
 *
 * **Warum das hier entschieden wird.** Seit der Code kein Anwenderfeld mehr ist
 * (R676), trägt das Feld `code` bei den meisten Objektarten den vergebenen
 * Schlüssel — `KRS-000003-17`. Ihn vor den Namen zu setzen, machte aus
 * *„Konzern"* ein *„KRS-000003-17 — Konzern"*: dieselbe Zeichenfolge, die
 * eine Zeile tiefer als Objekt-ID steht, und für einen Menschen keine Hilfe.
 *
 * **Bei drei Objektarten bleibt der Code stehen, und das ist richtig:**
 * Währung, Land und Sprache führen den Code einer **Weltnorm** als Schlüssel
 * (R681, R692). *„EUR — Euro"* und *„FR — Frankreich"* sagen mehr als der Name
 * allein — der Code steht so in jeder gelieferten Datei.
 *
 * Unterschieden wird an der **Form**: Vorsilbe, sechs Ziffern, zwei
 * Prüfziffern (`Objektkennung.Bilde`). Ein ISO-Code hat sie nicht.
 */
export function istVergebeneKennung(code: string): boolean {
  return /^[A-Z]+-\d{6}-\d{2}$/.test(code)
}

/**
 * Ein Element benennen: **Code und Name, wo der Code etwas sagt** (R699).
 *
 * **Eine Stelle für dieselbe Entscheidung.** Bis zum 2026-09-22 setzte
 * ein rundes Dutzend Bildschirme den Code selbst vor den Namen — jeder mit
 * seinem eigenen Trennzeichen, und jeder für sich. Als das Auswahlfeld lernte,
 * einen vergebenen Schlüssel wegzulassen, bekamen sie es nicht mit: Sie fragen
 * es ja nicht. Der Fund des Auftraggebers betraf die Journalleiste; dieselbe
 * Lücke stand an elf weiteren Stellen.
 *
 * **Wo der Code eine Nummer oder ein Kürzel ist, bleibt er stehen** —
 * `1 Laufende Geschäftstätigkeit` in der Kapitalflussrechnung, `EUR — Euro`
 * bei den Währungen. Nur der vergebene Schlüssel fällt weg, denn er steht am
 * Blatt ohnehin als Objekt-ID.
 *
 * **Seit dem 2026-09-22 entscheidet das der Dienst** (R705). Die Formprüfung
 * darunter greift nur noch bei zwei Objektarten — alle übrigen Schlüssel
 * stammen aus der Zeit vor der Vergabe und sehen lesbar aus. Der Dienst
 * schickt deshalb das Codefeld **leer**, wo die Objektart keines führt; was
 * hier ankommt, ist dann schon die Antwort. Die Formprüfung bleibt als
 * zweiter Boden für alles, was noch nicht umgestellt ist.
 */
/**
 * Ob ein Code neben dem Namen etwas sagt — oder nur unsere Kennung ist
 * (R699, R703).
 *
 * **Zwei Bedingungen, und beide müssen zutreffen**, damit er verschwindet:
 * Er ist der Schlüssel selbst, UND er sieht aus wie eine vergebene Kennung —
 * ein Kürzel, ein Bindestrich, etwas dahinter.
 *
 * **Der Schlüsselvergleich allein genügte nicht:** Eine Weltnorm trägt ihren
 * Code als Schlüssel — `EUR` ist beides —, und sie soll stehen bleiben
 * (R681, R692). **Das Muster allein genügte auch nicht:** Ein fremder
 * Kontenrahmen darf `AB-100` heißen, und dann ist es sein Code und nicht
 * unsere Kennung (R679).
 *
 * **Der Befund, aus dem sie entstand** (2026-09-25): In der Bilanz stand
 * `POS-A-II-1 A.II.1 Grundstücke …` — seit R676 ist der Schlüssel der Code,
 * und seit R678 trägt die Bezeichnung ihre Nummer selbst. Dieselbe Auskunft
 * zweimal, die zweite in einer Sprache, die dem Anwender nicht gehört.
 */
export function zeigtCode(code: string | null | undefined, schluessel?: string): boolean {
  if (!code) {
    return false
  }

  const wieEineKennung = /^[A-Z]{2,5}-\S/.test(code)
  if (schluessel !== undefined && code === schluessel && wieEineKennung) {
    return false
  }

  return !istVergebeneKennung(code)
}

export function mitCode(
  code: string | null | undefined,
  name: string,
  trenner = ' · ',
  schluessel?: string,
): string {
  /*
    **Was gleich dem Schlüssel ist, ist kein Code** (R699, R703).

    `istVergebeneKennung` kennt nur die vergebene Form `EH-000005-57`. Seit
    R676 tragen aber auch Position, Konto, Gliederung und Bewegungsart ihren
    Schlüssel als Code — `POS-A-II-1`, `KTO-0210`, `PLA-HGB` —, und der sieht
    lesbar aus, ohne auskunftsfähig zu sein. Wer ihn vor den Namen setzt,
    zeigt dem Anwender eine Sprache, die ihm nicht gehört.

    **Der Vergleich ist die Regel, nicht das Muster:** Wo der Aufrufer den
    Schlüssel mitgibt, entscheidet er — und was ihm gleicht, fällt weg.
  */
  const zeigen = zeigtCode(code, schluessel) ? code! : ''

  if (!zeigen) {
    // **Der Rückfall ist der Schlüssel, nicht der Code** (R705, R238). Seit der
    // Dienst das Codefeld leer schickt, wo es keines gibt, bliebe hier sonst
    // eine leere Zelle — bei einem Element, das noch keine Bezeichnung trägt.
    return name || schluessel || (code ?? '')
  }

  return name ? `${zeigen}${trenner}${name}` : zeigen
}

/**
 * Ein Konto, wie es in jeder Liste und jedem Satz steht (R699, R703).
 *
 * **Der Befund des Auftraggebers vom 2026-10-04:** In der Kontenauswahl der
 * Buchungsmaske stand `KTO-0100 0100 Geschäfts- oder Firmenwert` — der
 * Schlüssel vor einer Bezeichnung, die ihre Nummer seit R678 selbst trägt.
 * Die Regel dagegen stand seit dem 2026-09-25 in `mitCode`; die Maske, die
 * Prüferansicht und das Baumfenster haben sie umgangen, weil sie Nummer und
 * Name von Hand zusammensetzten.
 *
 * **Darum gibt es dafür genau diese eine Stelle.** Eine echte Kontonummer,
 * die nicht dem Schlüssel gleicht, bleibt stehen.
 */
export function kontoname(konto: { schluessel: string; nummer: string; namen: Beschriftung }): string {
  return mitCode(konto.nummer, beschriftung(konto.namen), ' ', konto.schluessel)
}

/**
 * Ab wann eine Aufzählung gesucht werden will: **zwölf** (R583).
 *
 * Keine gegriffene Zahl, sondern die Höhe, die eine Klappliste aufschlägt,
 * bevor sie zu rollen anfängt. In dem Augenblick, in dem sie rollt, ist sie
 * nicht mehr zu überblicken.
 */
const UEBERBLICK = 12

export function Auswahlfeld({
  wert,
  eintraege: alleEintraege,
  aufWahl,
  periode,
  fussnote,
  leer,
  id,
  className,
  beschriftung,
  titel,
  sorte = 'verweis',
  abgeschaltet = false,
  autoFocus = false,
}: {
  wert: string
  eintraege: Auswahleintrag[]
  aufWahl: (wert: string) => void
  /**
   * Die Periode, in der gewählt wird — ein Stillgelegtes fehlt ab ihr
   * (R1447). Ohne sie zählt der laufende Monat.
   */
  periode?: number
  /**
   * Welcher Sorte der Vorrat ist (R583).
   *
   * Ein **Verweisfeld** sucht immer — ohne Rücksicht auf seine heutige Länge,
   * weil dasselbe Feld beim Entwickler drei und beim Kunden zweihundert
   * Einträge hat. Eine **Aufzählung** sucht erst, wenn sie länger ist als
   * zwölf; darunter bleibt sie die Klappliste, die der Anwender kennt.
   */
  sorte?: 'verweis' | 'aufzaehlung'
  /**
   * Der Eintrag für „nichts gewählt" und sein Text — `—` oder
   * *Quelle wählen …*. Fehlt er, gibt es ihn nicht.
   */
  leer?: string
  id?: string
  /**
   * Ein Satz **unter der aufgeklappten Liste** — für das, was sie nicht führt
   * (R764).
   *
   * **Er steht dort und nicht daneben:** Wer etwas sucht, das die Liste nicht
   * hat, sucht in der Liste. Ein Hinweis in der Kontextzeile kostete eine
   * Zeile Bildschirm und stünde da, auch wenn niemand sucht.
   *
   * Nicht wählbar und nicht mitgezählt: Er ist eine Auskunft, kein Eintrag.
   */
  fussnote?: string
  className?: string
  /** `aria-label`, wo kein `<label>` danebensteht. */
  beschriftung?: string
  titel?: string
  abgeschaltet?: boolean
  autoFocus?: boolean
}) {
  // **Nur was in der Periode gilt** (R1447); ein schon gewähltes Stillgelegtes
  // bleibt mit Vermerk stehen.
  const eintraege = useMemo(() => angeboten(alleEintraege, wert, periode), [alleEintraege, wert, periode])
  const kennung = useId()
  const listenId = `${kennung}-liste`
  const feld = useRef<HTMLInputElement>(null)
  const rahmen = useRef<HTMLDivElement>(null)

  const [offen, setOffen] = useState(false)
  const [eingabe, setEingabe] = useState('')
  /**
   * Ob die Eingabe ein Filter ist.
   *
   * **Beim Aufklappen steht das Gewählte im Feld und ist markiert** (R586) —
   * die erste Taste ersetzt es, ohne dass jemand löschen müsste. Genau deshalb
   * darf dieser Text nicht als Filter gelten: Sonst zeigte die frisch
   * aufgeklappte Liste genau einen Eintrag, nämlich den, der schon gewählt ist.
   */
  const [getippt, setGetippt] = useState(false)
  const [zeiger, setZeiger] = useState(0)
  /** Ob der gewählte Name im zugeklappten Feld gekürzt dasteht — dann steht er ganz als Hinweis (R1701). */
  const [gekuerzt, setGekuerzt] = useState(false)
  const markiereBeimZeichnen = useRef(false)

  /** Der leere Eintrag steht vorn, wo es ihn gibt — wie die erste `<option>`. */
  const alle = useMemo<Auswahleintrag[]>(
    () => (leer === undefined ? eintraege : [{ wert: '', text: leer }, ...eintraege]),
    [eintraege, leer],
  )

  const gewaehlt = alle.find((e) => e.wert === wert) ?? null
  const anzeige = gewaehlt?.text ?? ''

  /**
   * **Gefiltert wird, nicht gewichtet** (R584).
   *
   * Mehrere Wörter sind ein UND in beliebiger Reihenfolge — `tls fr` findet
   * `EH-NL-FR-TLS`. Die Ordnung der Liste bleibt, es wird nur weggelassen: Eine
   * Liste, die sich bei jedem Buchstaben umsortiert, nimmt dem Anwender den
   * Eintrag aus der Hand, auf den er schon zielt.
   */
  const gefiltert = useMemo(() => {
    const worte = getippt ? normalisiere(eingabe).split(/\s+/).filter((w) => w.length > 0) : []
    if (worte.length === 0) {
      return alle
    }

    return alle.filter((e) => {
      const heu = normalisiere(`${e.text} ${e.suchtext ?? ''}`)
      return worte.every((w) => heu.includes(w))
    })
  }, [alle, eingabe, getippt])

  function oeffne(ersteTaste?: string) {
    if (abgeschaltet) {
      return
    }

    if (ersteTaste === undefined) {
      setEingabe(anzeige)
      setGetippt(false)
      setZeiger(Math.max(0, alle.findIndex((e) => e.wert === wert)))
      markiereBeimZeichnen.current = true
    } else {
      setEingabe(ersteTaste)
      setGetippt(true)
      setZeiger(0)
    }

    setOffen(true)
  }

  /** Schließen, ohne zu wählen — was gewählt war, bleibt gewählt (R585). */
  function schliesse() {
    setOffen(false)
    setGetippt(false)
    setEingabe('')
    feld.current?.focus()
  }

  function waehle(eintrag: Auswahleintrag | undefined) {
    if (!eintrag) {
      return
    }

    setOffen(false)
    setGetippt(false)
    setEingabe('')
    aufWahl(eintrag.wert)
    feld.current?.focus()
  }

  // Beim Aufklappen steht das Gewählte markiert im Feld: Die erste Taste
  // ersetzt es, das Weitertippen hängt an (R586).
  useLayoutEffect(() => {
    if (offen && markiereBeimZeichnen.current) {
      markiereBeimZeichnen.current = false
      feld.current?.focus()
      feld.current?.select()
    }
  }, [offen])

  /**
   * Wo die Liste steht und wie breit sie ist — **so breit wie ihr längster
   * Eintrag**, bis zum Fensterrand (R1701). Gemessen an der einen Stelle, die
   * auch die Kontoauswahl misst.
   */
  const ort = useListenort(offen, feld, rahmen, { mindestbreite: 240, hoechstens: 320, wenigstens: 120, kippAb: 160 }, gefiltert)

  // Der hervorgehobene Eintrag wird mitgerollt — sonst steht die aufgeschlagene
  // Liste am Anfang, während das Gewählte an Stelle 174 sitzt (R586).
  useEffect(() => {
    if (offen) {
      // `scrollIntoView` kennt jsdom nicht — geprüft wird das Rollen nicht,
      // und daran soll kein Prüffall scheitern.
      document.getElementById(`${kennung}-e${zeiger}`)?.scrollIntoView?.({ block: 'nearest' })
    }
  }, [offen, zeiger, kennung])

  // Ein Klick daneben schließt — und wählt nichts.
  useEffect(() => {
    if (!offen) {
      return
    }

    function daneben(e: MouseEvent) {
      const ziel = e.target as Node
      if (!feld.current?.contains(ziel) && !rahmen.current?.contains(ziel)) {
        setOffen(false)
        setGetippt(false)
        setEingabe('')
      }
    }

    document.addEventListener('mousedown', daneben)
    return () => document.removeEventListener('mousedown', daneben)
  }, [offen])

  function beiTaste(e: React.KeyboardEvent<HTMLInputElement>) {
    if (abgeschaltet) {
      return
    }

    if (!offen) {
      // Pfeil ab, Alt+Pfeil ab oder der erste Buchstabe klappen auf — die drei
      // Griffe, die eine Klappliste überall hat.
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault()
        oeffne()
        return
      }

      if (e.key.length === 1 && !e.ctrlKey && !e.altKey && !e.metaKey) {
        e.preventDefault()
        oeffne(e.key)
      }

      return
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setZeiger((z) => Math.min(gefiltert.length - 1, z + 1))
      return
    }

    if (e.key === 'ArrowUp') {
      e.preventDefault()
      setZeiger((z) => Math.max(0, z - 1))
      return
    }

    if (e.key === 'Home') {
      e.preventDefault()
      setZeiger(0)
      return
    }

    if (e.key === 'End') {
      e.preventDefault()
      setZeiger(Math.max(0, gefiltert.length - 1))
      return
    }

    if (e.key === 'Enter') {
      e.preventDefault()
      waehle(gefiltert[zeiger])
      return
    }

    if (e.key === 'Escape') {
      // Die Suche abzubrechen darf nichts geändert haben — und es darf auch
      // nicht den Dialog schließen, in dem das Feld steht.
      e.preventDefault()
      e.stopPropagation()
      schliesse()
      return
    }

    if (e.key === 'Tab') {
      // **Wer weiterspringt, hat nicht gewählt** (R585) — das ist der
      // Unterschied zu Eingabe, und er ist der Grund, warum es beide gibt.
      setOffen(false)
      setGetippt(false)
      setEingabe('')
    }
  }

  /*
    **Die kurze Aufzählung bleibt die Klappliste** (R583).

    Ein Suchfeld über *xlsx · csv · sql · lucanet* ist Aufwand ohne Ertrag und
    nimmt dem Anwender eine Bedienung, die er kennt. Die Entscheidung steht
    hier und nicht an den Aufrufstellen: Sonst stünde dieselbe Regel in einem
    Dutzend `? :` und liefe mit der Zeit auseinander.
  */
  if (sorte === 'aufzaehlung' && alle.length <= UEBERBLICK) {
    return (
      <select
        id={id}
        className={className}
        value={wert}
        disabled={abgeschaltet}
        autoFocus={autoFocus}
        title={titel}
        aria-label={beschriftung}
        onChange={(e) => aufWahl(e.target.value)}
      >
        {alle.map((e) => (
          <option key={e.wert || '(leer)'} value={e.wert}>
            {e.text}
          </option>
        ))}
      </select>
    )
  }

  const klassen = ['auswahlfeld', className].filter((k) => k).join(' ')

  return (
    <>
      <input
        ref={feld}
        id={id}
        type="text"
        role="combobox"
        className={klassen}
        value={offen ? eingabe : anzeige}
        readOnly={!offen}
        disabled={abgeschaltet}
        autoFocus={autoFocus}
        autoComplete="off"
        spellCheck={false}
        title={titel ?? (gekuerzt && !offen ? anzeige : undefined)}
        aria-label={beschriftung}
        aria-expanded={offen}
        aria-controls={offen ? listenId : undefined}
        aria-autocomplete="list"
        aria-activedescendant={offen && gefiltert.length > 0 ? `${kennung}-e${zeiger}` : undefined}
        onClick={() => (offen ? schliesse() : oeffne())}
        onKeyDown={beiTaste}
        // Passt der gewählte Name nicht ins Feld, steht er ganz als Hinweis (R1701).
        onMouseEnter={(e) => setGekuerzt(e.currentTarget.scrollWidth > e.currentTarget.clientWidth)}
        onChange={(e) => {
          setEingabe(e.target.value)
          setGetippt(true)
          setZeiger(0)
        }}
        onBlur={(e) => {
          // Der Klick auf einen Eintrag nimmt dem Feld kurz den Fokus. Wer
          // dabei schlösse, schlösse die Liste unter der Hand weg.
          if (!rahmen.current?.contains(e.relatedTarget as Node | null)) {
            setOffen(false)
            setGetippt(false)
            setEingabe('')
          }
        }}
      />

      {offen &&
        ort &&
        createPortal(
          <div
            ref={rahmen}
            className="auswahlliste"
            style={ort}
          >
            {gefiltert.length === 0 ? (
              /* Ein Satz mit dem gesuchten Wort darin, und sonst nichts (R585, R145). */
              <p className="leer" role="status">
                {t('auswahl.leer', { wort: eingabe.trim() })}
              </p>
            ) : (
              <ul id={listenId} role="listbox" aria-label={beschriftung}>
                {gefiltert.map((e, i) => (
                  <Fragment key={e.wert || '(leer)'}>
                  {/*
                    **Eine Überschrift, wo die Gruppe wechselt** (R1115) — ohne
                    Rolle, damit Vorleseprogramme sie nicht als Eintrag zählen
                    und ein Klick darauf nichts wählt.
                  */}
                  {e.gruppe && e.gruppe !== gefiltert[i - 1]?.gruppe && (
                    <li className="listengruppe" role="presentation">
                      {e.gruppe}
                    </li>
                  )}
                  <li
                    id={`${kennung}-e${i}`}
                    role="option"
                    /* Der Wert steht am Eintrag, nicht nur in der Beschriftung
                       — eine `<option>` trug ihn, und was eine Liste anbietet,
                       soll am Bildschirm ablesbar bleiben. */
                    data-wert={e.wert}
                    aria-selected={e.wert === wert}
                    className={i === zeiger ? 'zeiger' : undefined}
                    onMouseDown={(ev) => ev.preventDefault()}
                    onMouseEnter={() => setZeiger(i)}
                    onClick={() => waehle(e)}
                  >
                    {e.text}
                  </li>
                  </Fragment>
                ))}

                {/*
                  **Was die Liste nicht führt, sagt sie selbst** (R764).
                  Zuletzt und ohne Rolle: Vorleseprogramme zählen ihn nicht
                  als Eintrag mit, und ein Klick darauf wählt nichts aus.
                */}
                {fussnote && <li className="listenfuss">{fussnote}</li>}
              </ul>
            )}
          </div>,
          document.body,
        )}
    </>
  )
}
